import React from 'react';
import autobind from 'autobind-decorator';
import {Button, Col, FormControl, FormGroup, Row, Table} from 'react-bootstrap';

import {SurveyAnswerPlantQuantity} from "../../../Models/StreamBank/SurveyAnswerPlantQuantity";
import {SurveyAnswerPlantQuantitySeedMix} from "../../../Models/StreamBank/SurveyAnswerPlantQuantitySeedMix";

import {Glyphicon} from "../../Common/GlyphIcon";
import {SurveyAnswerPlantQuantityStockTypeFieldEditor} from "../../Common/Species";
import {SurveyAnswerPlantQuantityStockTypeFieldViewer} from "../../Common/Species";

import {service as SurveyAnswerPlantQuantityTypeService} from "../../../Services/SurveyAnswerPlantQuantityTypeService";
import {service as SpeciesInvasivityService} from "../../../Services/SpeciesInvasivityService";

import {
    GuidUtils,
    ObjectUtils
} from "../../../Utils";

import {
    SpeciesFieldEditor,
    SpeciesFieldViewer
} from "../../Common/Species";
import {usePrimeCache} from "../../../Hooks/ServiceHooks";

function getId(x, key) {
    return x[key] || x._tempId;
}

function getSurveyAnswerPlantQuantityId(x) {
    return getId(x, "surveyAnswerPlantQuantityId");
}

function getSurveyAnswerPlantQuantitySeedMixId(x) {
    return getId(x, "surveyAnswerPlantQuantitySeedMixId");
}

function getSurveyAnswerPlantQuantityZoneId(x) {
    return getId(x, "surveyAnswerPlantQuantityZoneId");
}

function PlantQuantity(props) {
    let {
        x,
        z,
        type,
        editMode,
        DeleteSubRow,
        UpdateSub
    } = props;
    
    usePrimeCache([SpeciesInvasivityService]);
    
    const speciesInvasivityId = SpeciesInvasivityService.CachedValueByProperty('Non-Invasive', 'name')?.result?.speciesInvasivityId;

    return <table
        className="sb-survey-form-child-table"
    >
        <tbody>
            <tr>
                <td
                    className="sb-child-table"
                    colSpan={2}
                >
                    <div className="sb-survey-form-table-header">
                        {
                            editMode ?
                                <div
                                    className="sb-survey-form-table-custom-controls float-right"
                                >
                                    <a onClick={() => DeleteSubRow(getSurveyAnswerPlantQuantityZoneId(z), getSurveyAnswerPlantQuantityId(x), type.surveyAnswerPlantQuantityTypeId)}>
                                        <Glyphicon glyph="remove-sign" />
                                    </a>
                                </div>:
                                null
                        }
                    </div>
                </td>
            </tr>
            <tr>
                <th>
                    Species Name
                </th>
                <td>
                    {
                        editMode ?
                            <SpeciesFieldEditor
                                Value={x.speciesId}
                                speciesTypeId={type.speciesTypeId}
                                speciesInvasivityId={speciesInvasivityId}
                                OnUpdate={(value) => UpdateSub(z, x, 'speciesId', value)}
                            />:
                            <SpeciesFieldViewer
                                Value={x.speciesId}
                            />
                    }
                </td>
            </tr>
            <tr className="separator"><th><div/></th><td></td></tr>
            <tr>
                <th>
                    Quantities
                </th>
                <td>
                    {
                        editMode ?
                            <FormControl
                                className="sb-survey-form-input-text"
                                type="number"
                                value={x.anticipatedQuantities}
                                onChange={(e) => UpdateSub(z, x, 'anticipatedQuantities', e.target.value)}
                            />:
                            <div>{x.anticipatedQuantities}</div>
                    }
                </td>
            </tr>
            <tr className="separator"><th><div/></th><td></td></tr>
            <tr>
                <th>
                    Stock Type
                </th>
                <td>
                    {
                        editMode ?
                            <SurveyAnswerPlantQuantityStockTypeFieldEditor
                                Value={x.anticipatedStockTypeId}
                                OnUpdate={(value) => UpdateSub(z, x, 'anticipatedStockTypeId', value)}
                            />:
                            <SurveyAnswerPlantQuantityStockTypeFieldViewer
                                Value={x.anticipatedStockTypeId}
                            />
                    }
                </td>
            </tr>
            <tr className="separator"><th><div/></th><td></td></tr>
            <tr>
                <th>
                    Spacing
                </th>
                <td>
                    <Row>
                        <Col xs={5}>
                            {
                                editMode ?
                                    <span><FormControl
                                        className="sb-survey-form-input-text"
                                        type="number"
                                        value={x.spacingMin}
                                        onChange={(e) => UpdateSub(z, x, 'spacingMin', Math.round(e.target.value * 10) / 10)}
                                    /><span className="feet-marker">ft</span></span>:
                                    <div>{x.spacingMin} ft</div>
                            }
                        </Col>
                        <Col xs={2}>-</Col>
                        <Col xs={5}>
                            {
                                editMode ?
                                    <span><FormControl
                                        className="sb-survey-form-input-text"
                                        type="number"
                                        value={x.spacingMax}
                                        onChange={(e) => UpdateSub(z, x, 'spacingMax', Math.round(e.target.value * 10) / 10)}
                                    /> <span className="feet-marker">ft</span></span>:
                                    <div>{x.spacingMax} ft</div>
                            }
                        </Col>
                    </Row>
                </td>
            </tr>
        </tbody>
    </table>;
}

function SeedMixRecord(props) {
    let {
        x,
        z,
        type,
        editMode,
        DeleteSubRow,
        UpdateSub,
        GetTotalSeedMix
    } = props;

    const speciesInvasivityId = SpeciesInvasivityService.CachedValueByProperty('Non-Invasive', 'name')?.result?.speciesInvasivityId;

    return <table
        className="sb-survey-form-child-table"
    >
        <tbody>
            <tr>
                <td
                    className="sb-child-table"
                    colSpan={2}
                >
                    <div className="sb-survey-form-table-header">
                        {
                            editMode ?
                                <div
                                    className="sb-survey-form-table-custom-controls float-right"
                                >
                                    <a onClick={() => DeleteSubRow(getSurveyAnswerPlantQuantityZoneId(z), getSurveyAnswerPlantQuantitySeedMixId(x), type.surveyAnswerPlantQuantityTypeId)}>
                                        <Glyphicon glyph="remove-sign" />
                                    </a>
                                </div>:
                                null
                        }
                    </div>
                </td>
            </tr>
            <tr>
                <th>
                    Species Name
                </th>
                <td>
                    {
                        editMode ?
                            <SpeciesFieldEditor
                                Value={x.speciesId}
                                speciesTypeId={type.speciesTypeId}
                                speciesInvasivityId={speciesInvasivityId}
                                OnUpdate={(value) => UpdateSub(z, x, 'speciesId', value)}
                            />:
                            <SpeciesFieldViewer
                                Value={x.speciesId}
                            />
                    }
                </td>
            </tr>
            <tr className="separator"><th><div/></th><td></td></tr>
            <tr>
                <th>
                    % Composition
                </th>
                <td>
                    {
                        editMode ?
                            <div>
                                <FormGroup validationState={GetTotalSeedMix(z) != 100 ? 'error' : null }>
                                    <FormControl
                                        className="sb-survey-form-input-text"
                                        style={{"width": "5em", "display": "inline"}}
                                        type="number"
                                        value={x.percent}

                                        onChange={(e) => UpdateSub(z, x, 'percent', e.target.value)}
                                    />
                                    {
                                        GetTotalSeedMix(z) > 100 ?
                                            <span className="sb-model-viewer-validation-failure-text-small">Composition Exceeds 100%</span>:
                                            null
                                    }
                                    {
                                        GetTotalSeedMix(z) < 100 ?
                                            <span className="sb-model-viewer-validation-failure-text-small">Composition is below 100%</span>:
                                            null
                                    }
                                </FormGroup>
                            </div>:
                            <div>{x.percent}</div>
                    }
                </td>
            </tr>
        </tbody>
    </table>;
}

export class PlantQuantities extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            types: []
        };
    }

    componentDidMount() {
        SurveyAnswerPlantQuantityTypeService.Get()
            .then((x) => {
                this.setState({types: x.result});
            });
    }

    @autobind
    update(record, field, value) {
        let customData = ObjectUtils.Clone(this.props.CustomData);
        let x = customData.PlantQuantities.find((x) => getSurveyAnswerPlantQuantityZoneId(x) === getSurveyAnswerPlantQuantityZoneId(record));
        if(x) {
            x[field] = value;
            this.props.OnCustomDataChange(customData);
        }
    }

    @autobind
    deleteRow(record) {
        let customData = ObjectUtils.Clone(this.props.CustomData);

        customData.PlantQuantities = customData.PlantQuantities.filter((x) => getSurveyAnswerPlantQuantityZoneId(x) !== getSurveyAnswerPlantQuantityZoneId(record));

        this.props.OnCustomDataChange(customData);
    }

    @autobind
    addNewRow(surveyAnswerPlantQuantityTypeId) {
        let customData = ObjectUtils.Clone(this.props.CustomData);

        customData.PlantQuantities.push({
            surveyAnswerPlantQuantityTypeId: surveyAnswerPlantQuantityTypeId,
            _tempId: GuidUtils.Generate(),
            SurveyAnswerPlantQuantities: [],
            SurveyAnswerPlantQuantitySeedMixes: []
        });

        this.props.OnCustomDataChange(customData);
    }

    @autobind
    addNewSubRow(surveyAnswerPlantQuantityZoneId, surveyAnswerPlantQuantityTypeId) {
        let customData = ObjectUtils.Clone(this.props.CustomData);
        let zone = customData.PlantQuantities.find((x) => getSurveyAnswerPlantQuantityZoneId(x) == surveyAnswerPlantQuantityZoneId);
        // TODO: after refactoring this to a sub component
        // base these values on properties of the component instead
        // and remove the surveyAnswerPlantQuantityTypeId parameter

        let modelJSON = {}; // TODO: use a method from the generated model to get an empty object

        let model = new SurveyAnswerPlantQuantity();
        let customDataKey= "SurveyAnswerPlantQuantities";
        if(this.state.types.find((type) => { return type.surveyAnswerPlantQuantityTypeId === surveyAnswerPlantQuantityTypeId && type.code === 'herbaceousSeed'}) != null) {
            model = new SurveyAnswerPlantQuantitySeedMix();
            customDataKey = "SurveyAnswerPlantQuantitySeedMixes";
            modelJSON.percent = this.getRemainingSeedMix(zone);
        }

        modelJSON._tempId = GuidUtils.Generate();

        zone[customDataKey].push(modelJSON);

        this.props.OnCustomDataChange(customData);
    }

    @autobind
    deleteSubRow(surveyAnswerPlantQuantityZoneId, surveyAnswerPlantQuantityId, surveyAnswerPlantQuantityTypeId) {
        let customData = ObjectUtils.Clone(this.props.CustomData);

        // TODO: after refactoring this to a sub component
        // base these values on properties of the component instead
        // and remove the surveyAnswerPlantQuantityTypeId parameter
        let model = new SurveyAnswerPlantQuantity();
        let customDataKey= "SurveyAnswerPlantQuantities";
        if(this.state.types.find((type) => { return type.surveyAnswerPlantQuantityTypeId === surveyAnswerPlantQuantityTypeId && type.code === 'herbaceousSeed'}) != null) {
            model = new SurveyAnswerPlantQuantitySeedMix();
            customDataKey = "SurveyAnswerPlantQuantitySeedMixes"
        }

        const modelPrimaryKeyProperty = model.getPrimaryKeyName();

        let zone = customData.PlantQuantities.find((x) => getSurveyAnswerPlantQuantityZoneId(x) == surveyAnswerPlantQuantityZoneId);
        zone[customDataKey] = zone[customDataKey].filter((x) => getId(x, modelPrimaryKeyProperty) !== surveyAnswerPlantQuantityId);

        this.props.OnCustomDataChange(customData);
    }

    @autobind
    updateSub(zone, record, field, value) {
        let customData = ObjectUtils.Clone(this.props.CustomData);
        let z = customData.PlantQuantities.find((x) => getSurveyAnswerPlantQuantityZoneId(x) === getSurveyAnswerPlantQuantityZoneId(zone));
        if(z) {
            let x = z.SurveyAnswerPlantQuantities.find((x) => getSurveyAnswerPlantQuantityId(x) === getSurveyAnswerPlantQuantityId(record));

            if(x) {
                x[field] = value;
                this.props.OnCustomDataChange(customData);
            }
        }
    }

    @autobind
    updateSubSeedMix(zone, record, field, value) {
        let customData = ObjectUtils.Clone(this.props.CustomData);
        let z = customData.PlantQuantities.find((x) => getSurveyAnswerPlantQuantityZoneId(x) === getSurveyAnswerPlantQuantityZoneId(zone));
        if(z) {
            let x = z.SurveyAnswerPlantQuantitySeedMixes.find((x) => getSurveyAnswerPlantQuantitySeedMixId(x) === getSurveyAnswerPlantQuantitySeedMixId(record));

            if(x) {
                x[field] = value;
                this.props.OnCustomDataChange(customData);
            }
        }
    }

    @autobind
    getTotalSeedMix(zone) {
        return zone.SurveyAnswerPlantQuantitySeedMixes.map((x) => Number(x.percent) || 0).reduce((a, b) => a + b, 0);
    }

    @autobind
    getRemainingSeedMix(zone) {
        let total = this.getTotalSeedMix(zone);
        return Math.max(100 - total, 0);
    }

    @autobind
    getTotalForZone(zone) {
        let ret = 0;

        let data = zone.SurveyAnswerPlantQuantities.filter((x) => !isNaN(x.anticipatedQuantities));
        if(data.length) {
            ret += data.map((x) => Number(x.anticipatedQuantities) || 0).reduce((a, b) => a + b);
        }

        return ret;
    }

    @autobind
    onSave() {
        if(this.props.OnSave) {
            this.props.OnSave();
        }
    }

    render() {
        return <Col key={this.props.Section.surveySectionId} id={this.props.id}>
            <Row>
                <Col xs={12}>
                    <h3>{this.props.Section.name}</h3>
                    {
                        this.props.Section.description ?
                            <h4>{this.props.Section.description}</h4>:
                            null
                    }
                </Col>
            </Row>
            {
                this.state.types.map((type) => <Row key={type.surveyAnswerPlantQuantityTypeId}>
                        <Col xs={12}>
                            <h4>{type.name}</h4>
                            <Table className="sb-survey-form-table sb-repeater-table" striped bordered>
                                <tbody>
                                    {   !this.props.EditMode && this.props.CustomData.PlantQuantities.filter((x) => x.surveyAnswerPlantQuantityTypeId == type.surveyAnswerPlantQuantityTypeId).length === 0 ?
                                        <tr>
                                            <td className="sb-child-table">
                                                <table className="sb-survey-form-child-table">
                                                    <tbody>
                                                    <tr>
                                                        <td className="sb-survey-form-table-custom-risks-value-area">
                                                            No planting zones defined.
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                        : null
                                    }
                                    {
                                        this.props.CustomData.PlantQuantities
                                            .filter((x) => x.surveyAnswerPlantQuantityTypeId == type.surveyAnswerPlantQuantityTypeId)
                                            .map((z) => <tr key={getSurveyAnswerPlantQuantityZoneId(z)}>
                                                <td className="sb-child-table" colSpan={2}>
                                                    <div className="sb-survey-form-table-header">
                                                        {
                                                            this.props.EditMode ?
                                                                <div
                                                                    className="sb-survey-form-table-custom-controls float-right"
                                                                >
                                                                    <a onClick={() => this.deleteRow(z)}>
                                                                        <Glyphicon glyph="remove-sign" />
                                                                    </a>
                                                                </div>:
                                                                null
                                                        }
                                                        <p>
                                                            <span>Zone: {z.name}</span>
                                                        </p>
                                                    </div>
                                                    <table className="sb-survey-form-child-table">
                                                        <tbody>
                                                            <tr>
                                                                <th>
                                                                    Zone Name
                                                                </th>
                                                                <td>
                                                                    {
                                                                        this.props.EditMode ?
                                                                            <FormControl
                                                                                className="sb-survey-form-input-text"
                                                                                type="text"
                                                                                value={z.name}
                                                                                onChange={(e) => this.update(z, 'name', e.target.value)}
                                                                            />:
                                                                            <div>{z.name}</div>
                                                                    }
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th>
                                                                    Acreage
                                                                </th>
                                                                <td>
                                                                    {
                                                                        this.props.EditMode ?
                                                                            <FormControl
                                                                                className="sb-survey-form-input-text"
                                                                                type="number"
                                                                                value={z.acreage}
                                                                                onChange={(e) => this.update(z, 'acreage', e.target.value)}
                                                                            />:
                                                                            <div>{z.acreage}</div>
                                                                    }
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th className="seed-mix-percentage"
                                                                    colSpan={2}>
                                                                    {
                                                                        type.code === "herbaceousSeed" ?
                                                                            <span>
                                                                                Seed Mix Components: <span
                                                                                className={this.getTotalSeedMix(z) == 100 ? null : "sb-model-viewer-validation-failure-text"}>{this.getTotalSeedMix(z)}%</span>
                                                                            </span>:
                                                                            <span>
                                                                                Total: <span>{this.getTotalForZone(z)}</span>
                                                                            </span>
                                                                    }
                                                                </th>
                                                            </tr>
                                                            <tr>
                                                                <td
                                                                    className={"sb-child-table" + (this.props.EditMode ? " editing": "")}
                                                                    colSpan={2}
                                                                >
                                                                    {
                                                                        type.code !== "herbaceousSeed" ? (
                                                                                z.SurveyAnswerPlantQuantities.length !== 0 ?
                                                                                    z.SurveyAnswerPlantQuantities.map((x) => <PlantQuantity
                                                                                        key={getSurveyAnswerPlantQuantityId(x)}
                                                                                        x={x}
                                                                                        z={z}
                                                                                        type={type}
                                                                                        editMode={this.props.EditMode}
                                                                                        DeleteSubRow={this.deleteSubRow}
                                                                                        UpdateSub={this.updateSub}
                                                                                    />):
                                                                                    <p className="sb-survey-form-child-table-empty-label">No {type.name} defined.</p>
                                                                            ):
                                                                            (
                                                                                z.SurveyAnswerPlantQuantitySeedMixes.length !== 0 ?
                                                                                    z.SurveyAnswerPlantQuantitySeedMixes.map((x) => <SeedMixRecord
                                                                                        key={getSurveyAnswerPlantQuantitySeedMixId(x)}
                                                                                        x={x}
                                                                                        z={z}
                                                                                        type={type}
                                                                                        editMode={this.props.EditMode}
                                                                                        DeleteSubRow={this.deleteSubRow}
                                                                                        UpdateSub={this.updateSubSeedMix}
                                                                                        GetTotalSeedMix={this.getTotalSeedMix}
                                                                                    />):
                                                                                    <p className="sb-survey-form-child-table-empty-label">No {type.name} defined.</p>
                                                                            )
                                                                    }
                                                                    {
                                                                        this.props.EditMode ?
                                                                            <div className="sb-mutable-list-add-section sb-survey-form-dynamic-question-add-row" onClick={() => this.addNewSubRow(getSurveyAnswerPlantQuantityZoneId(z), type.surveyAnswerPlantQuantityTypeId)}>
                                                                                <a><Glyphicon glyph="plus-sign" />Add {type.name}</a>
                                                                            </div>:
                                                                            null
                                                                    }
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )
                                    }
                                    {
                                        this.props.EditMode ?
                                            <tr className="sb-mutable-list-add-section sb-survey-form-dynamic-question-add-row" onClick={() => this.addNewRow(type.surveyAnswerPlantQuantityTypeId)}>
                                                <td colSpan={2}>
                                                    <a><Glyphicon glyph="plus-sign" />Add Zone</a>
                                                </td>
                                            </tr>:
                                            null
                                    }
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                )
            }
            <Row>
                <Col xs={12}>
                    {
                        this.props.EditMode ?
                            <Button onClick={this.onSave} variant="primary" className="float-right">
                                Save and Continue
                            </Button>:
                            null
                    }
                </Col>
            </Row>
        </Col>;
    }
}


